import { Link } from "@tanstack/react-router";
import {
  CheckCircle2,
  ChevronRight,
  ExternalLink,
  FileText,
  MinusCircle,
  PlusCircle,
  Users,
} from "lucide-react";
import { useEffect, useId, useState } from "react";
import { type BarItem, BarList } from "@/components/charts/BarList";
import { EditRoundSettingsDialog } from "@/components/reviews/EditRoundSettingsDialog";
import { RoundNotificationLog } from "@/components/reviews/RoundNotificationLog";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { DetailSection } from "@/components/shared/detail-section";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { OriginLink } from "@/components/shared/origin-link";
import { StatusPill, type StatusTone } from "@/components/shared/status-badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  type ExcludedReviewer,
  type ExclusionReason,
  type FormInUse,
  type ReviewCycle,
  type RosterPerson,
  type SignOffStatus,
  useConfidentialAnswers,
  useExcludedReviewers,
  useGenerateCycle,
  useGenerationPreview,
  useRemoveWorkloadAssignment,
  useReviewCycle,
  useReviewerAssignments,
  useReviewerProgress,
  useReviewerWorkload,
  useReviewSignOffStatus,
  useSetCycleParticipant,
  useSetReviewCycleStatus,
  useStragglers,
  useUpdateReviewCycle,
} from "@/hooks/use-review-cycles";
import { useReviewTemplates } from "@/hooks/use-review-templates";
import {
  type ReviewerRequestSummary,
  useDecideReviewerRequest,
  useReviewerRequestQueue,
} from "@/hooks/use-reviewer-requests";
import { useSession } from "@/lib/auth-client";
import { formatDateTime } from "@/lib/dates";
import { canViewAll } from "@/lib/permissions";

/**
 * One review round — EPIC-053 / EPIC-054.
 *
 * The screen is built around the thing that goes wrong: people who cannot be
 * placed. They are listed by name before anything is generated, and generating
 * without them takes a second, deliberate press.
 */

const STATUS_TONES: Record<string, StatusTone> = {
  draft: "neutral",
  open: "success",
  closed: "info",
};

/**
 * Why somebody is out, said as the thing to do about it.
 *
 * `fix` is what an admin has to change on the record; when it is null the
 * decision is theirs to make here and now.
 */
const EXCLUSION: Record<
  ExclusionReason,
  { label: string; fix: "person" | "form" | "role" | null }
> = {
  no_primary_role: { label: "No primary role", fix: "person" },
  no_matching_form: { label: "No form covers this role", fix: "form" },
  no_role_competencies: { label: "This role's competencies aren't loaded yet", fix: "role" },
  no_manager: { label: "No manager set", fix: "person" },
  employment_type: { label: "Not full time", fix: null },
  started_after_cutoff: { label: "Started after the cut-off", fix: null },
  not_named: { label: "Not named on this round", fix: null },
  excluded_by_admin: { label: "Taken out of this round", fix: null },
};

export function ReviewCycleDetail({ cycleId }: { cycleId: string }) {
  const cycle = useReviewCycle(cycleId);
  const preview = useGenerationPreview(cycleId);
  const generate = useGenerateCycle(cycleId);
  const setStatus = useSetReviewCycleStatus(cycleId);
  const participant = useSetCycleParticipant(cycleId);
  const [showAllIncluded, setShowAllIncluded] = useState(false);
  const [showAllExcluded, setShowAllExcluded] = useState(false);
  const [employmentFilter, setEmploymentFilter] = useState("all");
  const [reasonFilter, setReasonFilter] = useState("all");
  const [chosenTab, setChosenTab] = useState<RoundTab | null>(null);

  // Read here as well as in each section — the same cached queries — so the
  // tabs can carry counts and a tab with nothing in it can say so.
  const { data: session } = useSession();
  const isAdmin = canViewAll(session?.user.role);
  const statusKey = cycle.data?.statusKey;
  const stragglers = useStragglers(cycleId, statusKey === "open");
  const signOff = useReviewSignOffStatus(cycleId, statusKey !== undefined && statusKey !== "draft");
  const requests = useReviewerRequestQueue(cycleId);
  const confidential = useConfidentialAnswers(cycleId, isAdmin);

  if (cycle.isError) {
    return (
      <ErrorState
        message={cycle.error instanceof Error ? cycle.error.message : "Couldn't load this round"}
        onRetry={() => cycle.refetch()}
      />
    );
  }
  if (cycle.isLoading || !cycle.data) return <Skeleton className="h-64 w-full" />;

  const round = cycle.data;
  const roster = preview.data?.roster ?? [];
  const included = roster.filter((r) => r.included);
  const notIncluded = roster.filter((r) => !r.included);
  /**
   * A round of a kind that starts empty — the 90-day review (WI-314). Read
   * off the roster rather than the kind, so the screen and the generator can
   * never disagree about which rule is in force.
   */
  const startsEmpty = notIncluded.some((r) => r.reason === "not_named");
  const toGenerate = preview.data?.toGenerate ?? 0;
  const editable = round.statusKey !== "closed";

  const signOffRows = signOff.data ?? [];
  const requestRows = requests.data ?? [];
  const confidentialRows = confidential.data ?? [];
  const awaitingSignOff = signOffRows.filter((r) => r.awaiting !== "none").length;
  /**
   * One giant scrolling page became tabs. Only the tabs that can hold
   * something for this round's state are offered — a draft has sent no
   * email and has nothing to sign — and the People Ops tab only once there
   * is a confidential answer to read.
   */
  const tabs: RoundTabSpec[] = [
    { key: "people", label: "People", count: included.length },
    ...(round.statusKey === "draft"
      ? []
      : [
          {
            key: "progress" as const,
            label: "Progress",
            count: round.statusKey === "open" ? (stragglers.data ?? []).length : null,
            countLabel: "still waiting",
          },
          {
            key: "signoff" as const,
            label: "Sign-off",
            count: awaitingSignOff > 0 ? awaitingSignOff : null,
            countLabel: "awaiting",
          },
        ]),
    {
      key: "requests",
      label: "Requests",
      count: requestRows.length > 0 ? requestRows.length : null,
    },
    ...(round.statusKey === "draft"
      ? []
      : [{ key: "emails" as const, label: "Emails", count: null }]),
    ...(isAdmin && confidentialRows.length > 0
      ? [{ key: "confidential" as const, label: "People Ops only", count: confidentialRows.length }]
      : []),
  ];
  // Where the work is for the round's state: building it, chasing it, or
  // wrapping it up.
  const defaultTab: RoundTab =
    round.statusKey === "draft" ? "people" : round.statusKey === "open" ? "progress" : "signoff";
  const activeTab: RoundTab =
    chosenTab && tabs.some((t) => t.key === chosenTab) ? chosenTab : defaultTab;

  // Built from who is actually out, so a filter never offers an empty result.
  const employmentOptions = [...new Set(notIncluded.map((r) => r.employmentTypeName ?? "Unknown"))]
    .sort()
    .map((name) => ({ value: name, label: name }));
  const reasonOptions = [...new Set(notIncluded.map((r) => r.reason).filter((r) => r !== null))]
    .map((reason) => ({ value: reason, label: EXCLUSION[reason].label }))
    .sort((a, b) => a.label.localeCompare(b.label));
  const filteredNotIncluded = notIncluded.filter(
    (row) =>
      (employmentFilter === "all" || (row.employmentTypeName ?? "Unknown") === employmentFilter) &&
      (reasonFilter === "all" || row.reason === reasonFilter),
  );

  return (
    <div className="space-y-6">
      <RoundSection
        round={round}
        onOpen={() => setStatus.mutate("open")}
        onClose={() => setStatus.mutate("closed")}
      />

      <RoundTabs tabs={tabs} active={activeTab} onChange={setChosenTab} />

      {activeTab === "people" && (
        <div className="space-y-6">
          <FormsInUseSection
            round={round}
            formsInUse={preview.data?.formsInUse ?? []}
            peopleWithoutForm={preview.data?.peopleWithoutForm ?? 0}
            loading={preview.isLoading}
          />

          <DetailSection
            title="Who this round covers"
            headerActions={
              editable ? (
                <Button
                  size="sm"
                  // Never offer to generate off a count we could not read.
                  disabled={generate.isPending || preview.isError || toGenerate === 0}
                  onClick={() => generate.mutate()}
                >
                  {preview.isError
                    ? "Can't read this round"
                    : toGenerate === 0
                      ? "Nothing new to generate"
                      : `Generate ${toGenerate} review${toGenerate === 1 ? "" : "s"}`}
                </Button>
              ) : null
            }
          >
            {preview.isError ? (
              /*
              A failed preview used to fall through to `?? []` and `?? 0`, so the
              screen showed an empty roster, "Nothing new to generate" and a zero
              in every tile — indistinguishable from a correct answer, and an
              admin would reasonably read it as "this round covers nobody".
            */
              <ErrorState
                message={
                  preview.error instanceof Error
                    ? preview.error.message
                    : "Couldn't work out who this round covers"
                }
                onRetry={() => preview.refetch()}
              />
            ) : preview.isLoading ? (
              <Skeleton className="h-24 w-full" />
            ) : (
              <>
                <div className="mb-4 flex flex-wrap gap-2 text-sm">
                  <Stat icon={Users} label="In this round" value={included.length} />
                  <Stat
                    icon={CheckCircle2}
                    label="Already generated"
                    value={preview.data?.alreadyGenerated ?? 0}
                  />
                  {(preview.data?.byEmploymentType ?? []).map((type) => (
                    <Stat key={type.key} label={type.name} value={type.count} />
                  ))}
                </div>

                <RosterList
                  rows={included}
                  editable={editable}
                  emptyLabel="Nobody is in this round yet."
                  onDecide={(personId, decision) => participant.mutate({ personId, decision })}
                  showAll={showAllIncluded}
                  onToggleShowAll={() => setShowAllIncluded((shown) => !shown)}
                />
              </>
            )}
          </DetailSection>

          {notIncluded.length > 0 && (
            <DetailSection title={`Not in this round (${notIncluded.length})`}>
              {/*
              Two different stories, because they are two different rounds. One
              that covers everyone is explaining who fell out; one you build by
              hand is simply everybody you have not added yet, and telling THAT
              admin their records are missing a piece would be nonsense.
            */}
              <p className="mb-4 max-w-3xl text-muted-foreground text-sm">
                {startsEmpty
                  ? "This kind of round starts empty. Everybody is here until you add them — use “Add anyway” on the people it covers."
                  : "A round covers full-time people automatically. Everyone else is here: part-time staff and contractors to add case by case, and anyone whose record is missing a piece — set their primary role or manager, or make sure a form covers their role, and they join automatically."}
              </p>
              <p className="mb-4 max-w-3xl text-muted-foreground text-sm">
                {/*
                Said out loud because it is the thing nobody guesses: you do not
                put a form on a round or on a person. The first person to use
                this went looking for "add a form to this cycle" and there is no
                such action, because there is no such relationship.
              */}
                <span className="font-medium text-foreground">
                  Forms attach to roles, not to people.
                </span>{" "}
                Open a form and name the roles it covers, and everyone in those roles gets it. The
                round's fallback form covers anyone left over.
              </p>

              {/*
              Two filters rather than one: "show me the part-timers" and "show me
              everyone missing a manager" are different questions, and with a
              company-sized list both get asked.
            */}
              <div className="mb-4 flex flex-wrap gap-2">
                <FilterSelect
                  label="Employment type"
                  value={employmentFilter}
                  onChange={setEmploymentFilter}
                  options={employmentOptions}
                />
                <FilterSelect
                  label="Reason"
                  value={reasonFilter}
                  onChange={setReasonFilter}
                  options={reasonOptions}
                />
                {(employmentFilter !== "all" || reasonFilter !== "all") && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setEmploymentFilter("all");
                      setReasonFilter("all");
                    }}
                  >
                    Clear filters
                  </Button>
                )}
                <span className="self-center text-muted-foreground text-sm">
                  {filteredNotIncluded.length} of {notIncluded.length}
                </span>
              </div>

              <RosterList
                rows={filteredNotIncluded}
                editable={editable}
                emptyLabel="Nobody matches those filters."
                onDecide={(personId, decision) => participant.mutate({ personId, decision })}
                showAll={showAllExcluded}
                onToggleShowAll={() => setShowAllExcluded((shown) => !shown)}
              />
            </DetailSection>
          )}
        </div>
      )}

      {activeTab === "progress" && (
        <div className="space-y-6">
          <StragglersSection round={round} />
          <ReviewerProgressSection round={round} />
          <ReviewerWorkloadSection cycleId={round.id} />
          <ExcludedReviewersSection cycleId={round.id} />
        </div>
      )}

      {activeTab === "signoff" &&
        (signOffRows.length === 0 && !signOff.isLoading && !signOff.isError ? (
          <EmptyState
            title="Nothing to sign off yet"
            description="A review shows here once everyone asked has handed in, or the round has closed."
          />
        ) : (
          <SignOffSection round={round} />
        ))}

      {activeTab === "requests" &&
        (requestRows.length === 0 && !requests.isLoading && !requests.isError ? (
          <EmptyState
            title="No reviewer requests"
            description="When someone asks to review a colleague, or to come off a review, it lands here for you to decide."
          />
        ) : (
          <ReviewerRequestQueueSection cycleId={round.id} />
        ))}

      {activeTab === "emails" && <RoundNotificationLog round={round} />}

      {activeTab === "confidential" && <ConfidentialAnswersSection cycleId={round.id} />}
    </div>
  );
}

type RoundTab = "people" | "progress" | "signoff" | "requests" | "emails" | "confidential";

interface RoundTabSpec {
  key: RoundTab;
  label: string;
  /** Shown as a badge; null shows none. */
  count: number | null;
  /** Read out with the count, e.g. "3 still waiting". */
  countLabel?: string;
}

function RoundTabs({
  tabs,
  active,
  onChange,
}: {
  tabs: RoundTabSpec[];
  active: RoundTab;
  onChange: (tab: RoundTab) => void;
}) {
  return (
    <div
      role="tablist"
      aria-label="Round sections"
      className="flex flex-wrap gap-1 border-border border-b"
    >
      {tabs.map((tab) => {
        const selected = tab.key === active;
        return (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={selected}
            onClick={() => onChange(tab.key)}
            className={`-mb-px inline-flex items-center gap-2 border-b-2 px-3 py-2 font-medium text-sm transition-colors ${
              selected
                ? "border-primary text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {tab.label}
            {tab.count !== null && (
              <span
                className={`rounded-full px-1.5 py-0.5 text-2xs tabular-nums ${
                  selected ? "bg-primary text-primary-foreground" : "bg-muted text-foreground"
                }`}
              >
                {tab.count}
                {tab.countLabel && <span className="sr-only"> {tab.countLabel}</span>}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Which forms this round will draw on — the answer to "how do forms get
 * attached to a round".
 *
 * They are not attached. A form claims the roles and levels it covers, and
 * generation matches each person to one. That is easy to state and hard to
 * believe from a screen that never mentions it, so the matching is shown as a
 * result: these forms, these many people each, these many tasks.
 */
function FormsInUseSection({
  round,
  formsInUse,
  peopleWithoutForm,
  loading,
}: {
  round: ReviewCycle;
  formsInUse: FormInUse[];
  peopleWithoutForm: number;
  loading: boolean;
}) {
  const totalTasks = formsInUse.reduce((sum, form) => sum + form.taskCount, 0);

  return (
    <DetailSection
      title="Forms in use"
      headerActions={
        <Link
          to="/dashboard/reviews/forms"
          className="flex items-center gap-1 text-muted-foreground text-sm hover:underline"
        >
          All forms
          <ExternalLink className="size-3.5" aria-hidden />
        </Link>
      }
    >
      <p className="mb-4 max-w-3xl text-muted-foreground text-sm">
        Forms are not added to a round. Each form covers the roles and levels named on it, and
        everyone in this round is matched to one automatically. A review belongs to the person being
        reviewed; the people asked about them each get their own task, so{" "}
        {totalTasks === 0 ? "the task count" : `${totalTasks} tasks`} across{" "}
        {formsInUse.reduce((sum, form) => sum + form.subjectCount, 0)} people is normal.
      </p>

      {loading ? (
        <Skeleton className="h-24 w-full" />
      ) : formsInUse.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          No form covers anybody in this round yet.{" "}
          <Link to="/dashboard/reviews/forms" className="underline">
            Create one
          </Link>{" "}
          naming the roles it applies to, and the people it covers appear here.
        </p>
      ) : (
        <ul className="divide-y divide-border rounded-lg border border-border">
          {formsInUse.map((form) => (
            <li
              key={form.templateId}
              className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-x-4 gap-y-2 p-3"
            >
              <div className="min-w-0">
                <OriginLink
                  to="/dashboard/reviews/forms/$templateId"
                  params={{ templateId: form.templateId }}
                  className="font-medium hover:underline"
                >
                  {form.templateName}
                </OriginLink>
                {form.isFallback && (
                  <span className="ml-2 text-muted-foreground text-xs">
                    matched by role — this round doesn't name a form
                  </span>
                )}
                <div className="mt-1 flex flex-wrap items-center gap-1.5">
                  <span className="text-muted-foreground text-sm">Asks</span>
                  {form.asks.length === 0 ? (
                    <span className="text-sm text-warning-foreground">
                      nobody — this form generates no tasks
                    </span>
                  ) : (
                    form.asks.map((ask) => (
                      <StatusPill
                        key={ask.relationshipKey}
                        tone={ask.generatesToday ? "neutral" : "warning"}
                        label={ask.generatesToday ? ask.name : `${ask.name} — not generated yet`}
                      />
                    ))
                  )}
                </div>
              </div>
              <div className="justify-self-end text-right text-sm">
                <p className="font-medium tabular-nums">
                  {form.subjectCount} {form.subjectCount === 1 ? "person" : "people"}
                </p>
                <p className="text-muted-foreground tabular-nums">
                  {form.taskCount} {form.taskCount === 1 ? "task" : "tasks"}
                </p>
              </div>
            </li>
          ))}
        </ul>
      )}

      {peopleWithoutForm > 0 && (
        <p className="mt-3 text-sm text-warning-foreground">
          {peopleWithoutForm} {peopleWithoutForm === 1 ? "person is" : "people are"} in this round
          with no form covering their role. They are listed below and will generate nothing until a
          form covers them or a fallback is set.
        </p>
      )}

      <FallbackFormField round={round} />
    </DetailSection>
  );
}

/**
 * The form used for anybody no form claims.
 *
 * It exists in the data and had no screen, which left "No form covers this
 * role" as a dead end — the only way out was to go and write another form.
 */
function FallbackFormField({ round }: { round: ReviewCycle }) {
  const update = useUpdateReviewCycle(round.id);
  const { data } = useReviewTemplates();
  const editable = !round.generatedAt;
  const all = data ?? [];
  const current = all.find((template) => template.id === round.defaultTemplateId);
  /*
    Archived forms are not offered, but one already chosen stays in the list —
    otherwise the select silently shows nothing and the next save clears a
    setting nobody meant to touch.
  */
  const options = all.filter(
    (template) => template.status !== "archived" || template.id === round.defaultTemplateId,
  );

  if (!editable) {
    return (
      <p className="mt-4 border-border border-t pt-4 text-muted-foreground text-sm">
        Fallback form:{" "}
        {round.defaultTemplateId
          ? (current?.name ?? "a form that has since been removed")
          : "none — anyone no form covers is left out"}
      </p>
    );
  }

  return (
    <div className="mt-4 space-y-2 border-border border-t pt-4">
      <Label htmlFor={`fallback-${round.id}`}>Fallback form</Label>
      <p className="max-w-3xl text-muted-foreground text-sm">
        Used for anyone whose role no form covers. Leave it unset to keep those people out of the
        round rather than reviewing them against a form written for somebody else.
      </p>
      <Select
        value={round.defaultTemplateId ?? "none"}
        onValueChange={(value) =>
          update.mutate({ defaultTemplateId: value === "none" ? null : value })
        }
      >
        <SelectTrigger id={`fallback-${round.id}`} className="max-w-sm">
          <SelectValue placeholder="No fallback" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="none">No fallback</SelectItem>
          {options.map((template) => (
            <SelectItem key={template.id} value={template.id}>
              {template.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

/**
 * Who has not finished, for HR to chase.
 *
 * Only while the round is open — a closed round has nobody left to chase. Each
 * person carries the last thing they were emailed, so "have they been asked?"
 * is answered here rather than guessed, and somebody with no account is shown
 * rather than hidden: they will never receive a reminder however many passes
 * run, so they are precisely the ones needing a person.
 */
function StragglersSection({ round }: { round: ReviewCycle }) {
  const isOpen = round.statusKey === "open";
  const { data, isLoading, isError, error, refetch } = useStragglers(round.id, isOpen);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  if (!isOpen) return null;

  const rows = data ?? [];
  const allExpanded = rows.length > 0 && rows.every((r) => expanded.has(r.personId));
  const toggle = (personId: string) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(personId)) next.delete(personId);
      else next.add(personId);
      return next;
    });
  const chased: Record<string, string> = {
    invitation: "Invited",
    reminder: "Reminded",
    overdue: "Nudged — overdue",
  };

  return (
    <DetailSection
      title={`Still waiting on${rows.length > 0 ? ` (${rows.length})` : ""}`}
      headerActions={
        rows.length > 0 ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              setExpanded(allExpanded ? new Set() : new Set(rows.map((r) => r.personId)))
            }
          >
            {allExpanded ? "Collapse all" : "Expand all"}
          </Button>
        ) : null
      }
    >
      {isError ? (
        // "Nobody is outstanding" and "we could not find out" look identical
        // otherwise, and only one of them means the chasing is done.
        <ErrorState
          message={
            error instanceof Error ? error.message : "Couldn't load who is still outstanding"
          }
          onRetry={() => refetch()}
        />
      ) : isLoading ? (
        <Skeleton className="h-20 w-full" />
      ) : rows.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          Everybody has handed in. The round is ready to close.
        </p>
      ) : (
        <>
          <p className="mb-4 max-w-3xl text-muted-foreground text-sm">
            Reviewers are emailed when the round opens, again as the date nears, and once it passes.
            Anyone without an account can't be emailed at all and needs a word. Open a person to see
            whose reviews they still owe.
          </p>
          <ul className="divide-y divide-border rounded-lg border border-border">
            {rows.map((row) => {
              const open = expanded.has(row.personId);
              const panelId = `owes-${row.personId}`;
              return (
                <li key={row.personId}>
                  <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 p-3">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-7"
                      aria-expanded={open}
                      aria-controls={panelId}
                      aria-label={`${open ? "Hide" : "Show"} whose reviews ${row.personName} owes`}
                      onClick={() => toggle(row.personId)}
                    >
                      <ChevronRight
                        className={`size-4 transition-transform ${open ? "rotate-90" : ""}`}
                        aria-hidden
                      />
                    </Button>
                    <div className="min-w-0">
                      <OriginLink
                        to="/dashboard/persons/$personId"
                        params={{ personId: row.personId }}
                        className="font-medium hover:underline"
                      >
                        {row.personName}
                      </OriginLink>
                      <button
                        type="button"
                        className="block text-left text-muted-foreground text-sm hover:text-foreground"
                        onClick={() => toggle(row.personId)}
                      >
                        {row.outstanding} review{row.outstanding === 1 ? "" : "s"} outstanding
                      </button>
                    </div>
                    <StatusPill
                      tone={row.reachable ? "neutral" : "warning"}
                      label={
                        row.reachable
                          ? (chased[row.lastNotifiedKind ?? ""] ?? "Not emailed yet")
                          : "No account — can't be emailed"
                      }
                    />
                  </div>
                  {open && (
                    <ul id={panelId} className="space-y-1 px-3 pb-3 pl-13">
                      {row.owes.map((owed) => (
                        <li
                          key={owed.reviewerId}
                          className="flex flex-wrap items-baseline justify-between gap-2 rounded-md bg-muted/50 px-3 py-1.5 text-sm"
                        >
                          <span className="font-medium">{owed.subjectName}</span>
                          <span className="text-muted-foreground text-xs">
                            {owed.relationshipName}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </>
      )}
    </DetailSection>
  );
}

/**
 * How many reviews each person was asked to do in this round, and how much
 * is actually done — EPIC-073 WI-277. A different angle from the expandable
 * `ReviewerWorkloadSection` below (EPIC-059 WI-316, assignment counts for
 * rebalancing): this is a glance-able completion chart, shown however the
 * round stands, closed included — an unevenly loaded round is a fact about
 * generation, not about who still owes work.
 */
function ReviewerProgressSection({ round }: { round: ReviewCycle }) {
  const { data, isLoading, isError, error, refetch } = useReviewerProgress(round.id);

  const items: BarItem[] = (data ?? [])
    .slice(0, 10)
    .map((row) => ({ label: row.personName, value: row.total }));

  return (
    <DetailSection title="Review completion by reviewer">
      {isError ? (
        <ErrorState
          message={error instanceof Error ? error.message : "Couldn't load reviewer workload"}
          onRetry={() => refetch()}
        />
      ) : isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : items.length === 0 ? (
        <EmptyState
          title="Nobody has a reviewer assignment yet"
          description="Generate the round to see how the work is spread across reviewers."
        />
      ) : (
        <>
          <p className="mb-4 max-w-3xl text-muted-foreground text-sm">
            How many reviews each person has been asked to do — self, manager, peer and direct
            report all counted together. Somebody excluded from a review carries none of its weight
            here.
          </p>
          <BarList title="Reviews assigned per reviewer" items={items} />
        </>
      )}
    </DetailSection>
  );
}

const SIGN_OFF_FILTER_LABELS: Record<string, string> = {
  manager: "Awaiting manager",
  employee: "Awaiting employee",
  both: "Awaiting both",
  none: "Fully signed",
};

/**
 * Who is still waiting on a sign-off, and from which side — EPIC-065 WI-240.
 *
 * Fetched once a round has moved past `draft` (a review can reach complete
 * before the round itself closes), and rendered as nothing at all — not an
 * empty-state message — while there is genuinely nothing eligible yet, so a
 * round that just opened doesn't show a confusing blank section.
 */
function SignOffSection({ round }: { round: ReviewCycle }) {
  const [filter, setFilter] = useState("all");
  const { data, isLoading, isError, error, refetch } = useReviewSignOffStatus(
    round.id,
    round.statusKey !== "draft",
  );
  const rows = data ?? [];
  if (!isLoading && !isError && rows.length === 0) return null;

  const filtered = filter === "all" ? rows : rows.filter((r) => r.awaiting === filter);

  return (
    <DetailSection
      title="Sign-off"
      headerActions={
        <FilterSelect
          label="Status"
          value={filter}
          onChange={setFilter}
          options={Object.entries(SIGN_OFF_FILTER_LABELS).map(([value, label]) => ({
            value,
            label,
          }))}
        />
      }
    >
      {isError ? (
        <ErrorState
          message={error instanceof Error ? error.message : "Couldn't load sign-off status"}
          onRetry={() => refetch()}
        />
      ) : isLoading ? (
        <Skeleton className="h-20 w-full" />
      ) : filtered.length === 0 ? (
        <p className="text-muted-foreground text-sm">Nobody matches that filter.</p>
      ) : (
        <ul className="divide-y divide-border rounded-lg border border-border">
          {filtered.map((row) => (
            <SignOffRow key={row.reviewId} row={row} />
          ))}
        </ul>
      )}
    </DetailSection>
  );
}

function SignOffRow({ row }: { row: SignOffStatus }) {
  const tone: StatusTone = row.awaiting === "none" ? "success" : "neutral";
  return (
    <li className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4 p-3">
      <div className="min-w-0">
        <OriginLink
          to="/dashboard/reviews/reports/$reviewId"
          params={{ reviewId: row.reviewId }}
          className="font-medium hover:underline"
        >
          {row.subjectName}
        </OriginLink>
        <p className="text-muted-foreground text-sm">
          {row.daysOutstanding === 0
            ? "Became eligible today"
            : `Outstanding ${row.daysOutstanding} day${row.daysOutstanding === 1 ? "" : "s"}`}
        </p>
      </div>
      <StatusPill tone={tone} label={SIGN_OFF_FILTER_LABELS[row.awaiting] ?? row.awaiting} />
    </li>
  );
}

/**
 * Every excluded reviewer across the round — EPIC-057 WI-204, matching the
 * legacy "Excluded Tasks" page's own shape: a read-only list that links out
 * to the review where the actual toggle lives, rather than duplicating it
 * here.
 */
/**
 * Answers to the questions a form marked People-Ops-only — WI-281.
 *
 * Without this the setting is write-only: those answers are deliberately kept
 * off the review report, so a manager writing about promotion readiness would
 * be writing into nothing. Here is where People Ops read them.
 *
 * Admin-only, server-side. The section is absent for anyone else rather than
 * empty, so its existence is not itself a disclosure.
 */
function ConfidentialAnswersSection({ cycleId }: { cycleId: string }) {
  const { data: session } = useSession();
  const isAdmin = canViewAll(session?.user.role);
  const { data, isLoading, isError, error, refetch } = useConfidentialAnswers(cycleId, isAdmin);
  const rows = data ?? [];
  if (!isAdmin) return null;
  if (!isLoading && !isError && rows.length === 0) return null;

  return (
    <DetailSection title={`People Ops only (${rows.length})`}>
      <p className="mb-4 max-w-3xl text-muted-foreground text-sm">
        Answers to questions this round's form marked People Ops only. They are not on anyone's
        review report, and the person being reviewed never sees them.
      </p>
      {isError ? (
        <ErrorState
          message={error instanceof Error ? error.message : "Couldn't load the People Ops answers"}
          onRetry={() => refetch()}
        />
      ) : isLoading ? (
        <Skeleton className="h-20 w-full" />
      ) : (
        <ul className="divide-y divide-border rounded-lg border border-border">
          {rows.map((row) => (
            <li key={`${row.reviewId}-${row.reviewerName}-${row.question}`} className="p-3">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <p className="font-medium">
                  <OriginLink
                    to="/dashboard/reviews/reports/$reviewId"
                    params={{ reviewId: row.reviewId }}
                    className="hover:underline"
                  >
                    {row.subjectName}
                  </OriginLink>
                  <span className="ml-2 font-normal text-muted-foreground text-sm">
                    {row.roleName}
                    {row.roleLevelName ? ` · ${row.roleLevelName}` : ""}
                  </span>
                </p>
                {row.rating && <StatusPill tone="info" label={row.rating} />}
              </div>
              <p className="mt-1 text-muted-foreground text-sm">
                {row.question} — answered by {row.reviewerName} ({row.relationshipName})
              </p>
              {row.comment && <p className="mt-2 whitespace-pre-line text-sm">{row.comment}</p>}
            </li>
          ))}
        </ul>
      )}
    </DetailSection>
  );
}

/**
 * Reviewer totals for the round — EPIC-059 WI-316. Kori: leaders and people
 * rolled across several projects get handed an unrealistic number, and she
 * strips assignments out by hand once she sees it — this is that view,
 * heaviest first, expandable to who, and actionable from there.
 */
/**
 * HR's approval queue — EPIC-058 WI-208. One queue for both request types;
 * a decided request stays listed (retained with its outcome, AC-592) rather
 * than vanishing, so the section only hides itself when there have never
 * been any requests at all.
 */
function ReviewerRequestQueueSection({ cycleId }: { cycleId: string }) {
  const { data, isLoading, isError, error, refetch } = useReviewerRequestQueue(cycleId);
  const rows = data ?? [];
  if (!isLoading && !isError && rows.length === 0) return null;

  return (
    <DetailSection title="Reviewer requests">
      {isError ? (
        <ErrorState
          message={error instanceof Error ? error.message : "Couldn't load reviewer requests"}
          onRetry={() => refetch()}
        />
      ) : isLoading ? (
        <Skeleton className="h-20 w-full" />
      ) : (
        <ul className="divide-y divide-border rounded-lg border border-border">
          {rows.map((r) => (
            <ReviewerRequestRow key={r.id} cycleId={cycleId} request={r} />
          ))}
        </ul>
      )}
    </DetailSection>
  );
}

function ReviewerRequestRow({
  cycleId,
  request,
}: {
  cycleId: string;
  request: ReviewerRequestSummary;
}) {
  const pending = request.statusKey === "pending";
  return (
    <li className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-4 p-3">
      <div className="min-w-0">
        <p className="font-medium text-sm">
          {request.requesterName}{" "}
          {request.requestType === "add"
            ? `wants to review ${request.subjectName}`
            : `wants off ${request.subjectName}'s review`}
        </p>
        <p className="truncate text-muted-foreground text-sm">{request.reason}</p>
        <p className="mt-1 text-muted-foreground text-xs">{formatDateTime(request.createdAt)}</p>
        {request.decisionNote && (
          <p className="mt-1 text-muted-foreground text-xs">Note: {request.decisionNote}</p>
        )}
      </div>
      {pending ? (
        <div className="flex items-center gap-2">
          <DecideRequestButton cycleId={cycleId} requestId={request.id} decision="declined" />
          <DecideRequestButton cycleId={cycleId} requestId={request.id} decision="approved" />
        </div>
      ) : (
        <StatusPill
          tone={request.statusKey === "approved" ? "success" : "neutral"}
          label={request.statusKey === "approved" ? "Approved" : "Declined"}
        />
      )}
    </li>
  );
}

function DecideRequestButton({
  cycleId,
  requestId,
  decision,
}: {
  cycleId: string;
  requestId: string;
  decision: "approved" | "declined";
}) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const decide = useDecideReviewerRequest(cycleId);
  const approving = decision === "approved";

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setNote("");
      }}
    >
      <DialogTrigger asChild>
        <Button
          variant={approving ? "default" : "outline"}
          size="sm"
          className={approving ? undefined : "text-muted-foreground"}
        >
          {approving ? "Approve" : "Decline"}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{approving ? "Approve this request?" : "Decline this request?"}</DialogTitle>
          <DialogDescription>
            {approving
              ? "Applies it immediately — excludes the reviewer, or adds them, exactly as a direct roster edit would."
              : "The roster stays exactly as it is."}
          </DialogDescription>
        </DialogHeader>
        <div>
          <Label className="mb-1.5 block" htmlFor={`decision-note-${requestId}-${decision}`}>
            Note (optional)
          </Label>
          <Textarea
            id={`decision-note-${requestId}-${decision}`}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
          />
        </div>
        <DialogFooter>
          <Button
            disabled={decide.isPending}
            onClick={() =>
              decide.mutate(
                { requestId, decision, decisionNote: note.trim() || null },
                { onSuccess: () => setOpen(false) },
              )
            }
          >
            {approving ? "Approve" : "Decline"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReviewerWorkloadSection({ cycleId }: { cycleId: string }) {
  const { data, isLoading, isError, error, refetch } = useReviewerWorkload(cycleId);
  const [expandedPersonId, setExpandedPersonId] = useState<string | null>(null);
  const rows = data ?? [];
  if (!isLoading && !isError && rows.length === 0) return null;

  return (
    <DetailSection title="Reviewer workload">
      {isError ? (
        <ErrorState
          message={error instanceof Error ? error.message : "Couldn't load reviewer workload"}
          onRetry={() => refetch()}
        />
      ) : isLoading ? (
        <Skeleton className="h-20 w-full" />
      ) : (
        <ul className="divide-y divide-border rounded-lg border border-border">
          {rows.map((row) => (
            <WorkloadRow
              key={row.reviewerPersonId}
              cycleId={cycleId}
              row={row}
              expanded={expandedPersonId === row.reviewerPersonId}
              onToggle={() =>
                setExpandedPersonId((id) =>
                  id === row.reviewerPersonId ? null : row.reviewerPersonId,
                )
              }
            />
          ))}
        </ul>
      )}
    </DetailSection>
  );
}

function WorkloadRow({
  cycleId,
  row,
  expanded,
  onToggle,
}: {
  cycleId: string;
  row: { reviewerPersonId: string; reviewerName: string; assignedCount: number };
  expanded: boolean;
  onToggle: () => void;
}) {
  const assignments = useReviewerAssignments(cycleId, expanded ? row.reviewerPersonId : null);
  const remove = useRemoveWorkloadAssignment(cycleId);

  return (
    <li className="p-3">
      <button
        type="button"
        className="grid w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-4 text-left"
        onClick={onToggle}
      >
        <p className="font-medium">{row.reviewerName}</p>
        <StatusPill
          tone={row.assignedCount >= 10 ? "warning" : "neutral"}
          label={`${row.assignedCount} ${row.assignedCount === 1 ? "review" : "reviews"}`}
        />
      </button>
      {expanded && (
        <ul className="mt-2 space-y-1 border-t pt-2">
          {(assignments.data ?? []).map((a) => (
            <li key={a.reviewerId} className="flex items-center justify-between gap-2 py-1 text-sm">
              <OriginLink
                to="/dashboard/reviews/reports/$reviewId"
                params={{ reviewId: a.reviewId }}
                className="hover:underline"
              >
                {a.relationshipName}: {a.subjectName}
              </OriginLink>
              <ConfirmDialog
                title="Remove this assignment?"
                description="Takes them off this review. Their answers are kept, and this can be undone from the review itself."
                confirmLabel="Remove"
                destructive
                onConfirm={() => remove.mutate({ reviewId: a.reviewId, reviewerId: a.reviewerId })}
                trigger={
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-1.5 text-muted-foreground text-xs"
                    disabled={remove.isPending}
                  >
                    Remove
                  </Button>
                }
              />
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

function ExcludedReviewersSection({ cycleId }: { cycleId: string }) {
  const { data, isLoading, isError, error, refetch } = useExcludedReviewers(cycleId);
  const rows = data ?? [];
  if (!isLoading && !isError && rows.length === 0) return null;

  return (
    <DetailSection title="Excluded reviewers">
      {isError ? (
        <ErrorState
          message={error instanceof Error ? error.message : "Couldn't load excluded reviewers"}
          onRetry={() => refetch()}
        />
      ) : isLoading ? (
        <Skeleton className="h-20 w-full" />
      ) : (
        <ul className="divide-y divide-border rounded-lg border border-border">
          {rows.map((row) => (
            <ExcludedReviewerRow key={row.reviewerId} row={row} />
          ))}
        </ul>
      )}
    </DetailSection>
  );
}

function ExcludedReviewerRow({ row }: { row: ExcludedReviewer }) {
  return (
    <li className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4 p-3">
      <div className="min-w-0">
        <p className="font-medium">
          {row.relationshipName}: {row.reviewerName}
        </p>
        <p className="text-muted-foreground text-sm">
          On{" "}
          <OriginLink
            to="/dashboard/reviews/reports/$reviewId"
            params={{ reviewId: row.reviewId }}
            className="hover:underline"
          >
            {row.subjectName}
          </OriginLink>
          's review · excluded {formatDateTime(row.excludedAt)}
        </p>
      </div>
      <StatusPill tone="warning" label="Excluded" />
    </li>
  );
}

/**
 * One side of the roster. The same component for both, because the actions are
 * the same question from either direction — is this person in this round.
 */
function RosterList({
  rows,
  editable,
  emptyLabel,
  onDecide,
  showAll,
  onToggleShowAll,
}: {
  rows: RosterPerson[];
  editable: boolean;
  emptyLabel: string;
  onDecide: (personId: string, decision: "include" | "exclude" | null) => void;
  showAll: boolean;
  onToggleShowAll: () => void;
}) {
  const shown = showAll ? rows : rows.slice(0, 10);

  return (
    <>
      <ul className="divide-y divide-border rounded-lg border border-border">
        {rows.length === 0 && <li className="p-3 text-muted-foreground text-sm">{emptyLabel}</li>}
        {shown.map((row) => {
          const exclusion = row.reason ? EXCLUSION[row.reason] : null;
          return (
            <li
              key={row.personId}
              /*
                A three-part row with its own columns: who, what state, what you
                can do. Everything used to sit in one run of similar-weight
                text, so the actions read as more of the sentence rather than as
                controls.
              */
              className="grid grid-cols-[1fr_auto] items-center gap-x-4 gap-y-2 p-3 transition-colors hover:bg-muted/40 sm:grid-cols-[minmax(0,1fr)_auto_auto]"
            >
              <div className="min-w-0">
                <OriginLink
                  to="/dashboard/persons/$personId"
                  params={{ personId: row.personId }}
                  className="font-medium hover:underline"
                >
                  {row.personName}
                </OriginLink>
                {/*
                  The role sits UNDER the name rather than beside it: it is the
                  person's detail, not a column of its own, and stacking leaves
                  the row's middle free for the one thing that varies.

                  No role at all means the badge beside it already says "No
                  primary role" — saying it twice reads as two separate facts.
                */}
                {(row.roleName || row.employmentTypeName) && (
                  <p className="truncate text-muted-foreground text-sm">
                    {row.roleName}
                    {row.roleLevelName ? ` · ${row.roleLevelName}` : ""}
                    {/*
                      The employment type earns a place here now that it is one
                      of the reasons somebody is out — "Priya Raman · Part time"
                      answers the question the badge raises.
                    */}
                    {row.employmentTypeName
                      ? `${row.roleName ? " · " : ""}${row.employmentTypeName}`
                      : ""}
                  </p>
                )}
                {/*
                  The form each person will actually be reviewed against.
                  It was already worked out per person and then dropped before
                  rendering, which is most of why the matching felt invisible —
                  seeing it named on the row is what makes it checkable.
                */}
                {row.templateName && (
                  <p className="truncate text-muted-foreground text-xs">
                    <FileText className="mr-1 inline size-3" aria-hidden />
                    {row.templateName}
                    {row.templateIsFallback ? " (fallback)" : ""}
                  </p>
                )}
              </div>

              {/*
                Only states worth a badge: why somebody is out, or that their
                review already exists. "About to be generated" is what the
                button above already says, for the whole list at once.
              */}
              <div className="justify-self-start sm:justify-self-end">
                {exclusion ? (
                  <StatusPill
                    tone={row.reason === "excluded_by_admin" ? "neutral" : "warning"}
                    label={exclusion.label}
                  />
                ) : row.isNew ? null : (
                  <StatusPill tone="success" label="Generated" />
                )}
              </div>

              {/*
                One fixed-width action slot per row, so the buttons line up down
                the list instead of landing wherever the text above them ends.
                Outline rather than ghost: these are the things you can actually
                press, and they should look it.
              */}
              {editable ? (
                <div className="col-span-2 flex flex-wrap justify-start gap-2 sm:col-span-1 sm:justify-end">
                  {/*
                    Both can apply at once, and both are offered: somebody with
                    no manager can have one set, OR be reviewed on a self
                    assessment alone. Only a genuinely missing link — no primary
                    role, no form — has nothing but the fix, because there is
                    nothing to generate from until the record changes.
                  */}
                  {exclusion?.fix === "person" && (
                    <Button variant="outline" size="sm" asChild>
                      {/*
                        OriginLink, so their record shows "← Back to <round>"
                        and the trip is a round trip — the whole point of
                        sending somebody off to fix something.
                      */}
                      <OriginLink
                        to="/dashboard/persons/$personId"
                        params={{ personId: row.personId }}
                      >
                        <ExternalLink className="size-4" aria-hidden />
                        Update user record
                      </OriginLink>
                    </Button>
                  )}
                  {exclusion?.fix === "form" && (
                    <Button variant="outline" size="sm" asChild>
                      {/*
                        "Cover this role", not "Add a form": a form is never
                        attached to a person. It names the roles it covers, and
                        the round works out who gets which. The old label
                        promised a per-person action that does not exist, and
                        the forms list it lands on then reads as "create a new
                        one" — which is rarely what is wanted.
                      */}
                      <Link to="/dashboard/reviews/forms">
                        <ExternalLink className="size-4" aria-hidden />
                        Cover this role
                      </Link>
                    </Button>
                  )}
                  {exclusion?.fix === "role" && row.roleId && (
                    <Button variant="outline" size="sm" asChild>
                      {/*
                        The questions ARE the role's competencies, so the fix
                        is on the role, not on the person and not on the form.

                        Every role HAS competencies — Kori is still authoring
                        them, and they land in the app role by role (Matt,
                        2026-10-06). So this is "not loaded yet", never "this
                        role doesn't need any", and the link goes where the
                        loading happens.
                      */}
                      <OriginLink to="/dashboard/roles/$roleId" params={{ roleId: row.roleId }}>
                        <ExternalLink className="size-4" aria-hidden />
                        Set this role's competencies
                      </OriginLink>
                    </Button>
                  )}
                  {/*
                    Two actions, not three: the button says what you want, and
                    the server works out whether that needs an override or the
                    removal of one. Somebody an admin took out therefore gets
                    "Add anyway" back, which is the path an "Undo" used to own.
                  */}
                  {/*
                    Once a review exists, reading it is the likelier thing to
                    want — so it leads, and removing follows.
                  */}
                  {row.reviewId && (
                    <Button variant="outline" size="sm" asChild>
                      <OriginLink
                        to="/dashboard/reviews/reports/$reviewId"
                        params={{ reviewId: row.reviewId }}
                      >
                        <FileText className="size-4" aria-hidden />
                        Review
                      </OriginLink>
                    </Button>
                  )}
                  {row.included ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onDecide(row.personId, "exclude")}
                      aria-label={`Remove ${row.personName} from this round`}
                    >
                      <MinusCircle className="size-4" aria-hidden />
                      Remove
                    </Button>
                  ) : row.canIncludeByDecision ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onDecide(row.personId, "include")}
                      aria-label={`Add ${row.personName} to this round`}
                    >
                      <PlusCircle className="size-4" aria-hidden />
                      Add anyway
                    </Button>
                  ) : null}
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>

      {rows.length > 10 && (
        <Button variant="ghost" size="sm" className="mt-2" onClick={onToggleShowAll}>
          {showAll ? "Show fewer" : `Show all ${rows.length}`}
        </Button>
      )}
    </>
  );
}

/** A compact labelled filter. "All" is the first option and the default. */
function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger size="sm" className="w-52" aria-label={label}>
        <SelectValue placeholder={label} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">{label}: all</SelectItem>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-muted-foreground text-xs uppercase tracking-wide">{label}</dt>
      <dd className="mt-0.5">{value}</dd>
    </div>
  );
}

function Stat({ icon: Icon, label, value }: { icon?: typeof Users; label: string; value: number }) {
  return (
    <span className="flex items-center gap-1.5 rounded-md bg-muted px-2.5 py-1">
      {Icon && <Icon className="size-3.5 text-muted-foreground" aria-hidden />}
      <span className="font-medium tabular-nums">{value}</span>
      <span className="text-muted-foreground">{label}</span>
    </span>
  );
}

/**
 * The round's own facts — editable dates while it is still a draft, read-only
 * the moment reviews exist, because moving the period afterwards would
 * re-describe what everybody was rated on.
 */
function RoundSection({
  round,
  onOpen,
  onClose,
}: {
  round: ReviewCycle;
  onOpen: () => void;
  onClose: () => void;
}) {
  const update = useUpdateReviewCycle(round.id);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const ids = { start: useId(), end: useId(), opens: useId(), due: useId() };
  const [dates, setDates] = useState({
    periodStart: round.periodStart,
    periodEnd: round.periodEnd,
    opensOn: round.opensOn,
    dueOn: round.dueOn,
  });

  useEffect(() => {
    setDates({
      periodStart: round.periodStart,
      periodEnd: round.periodEnd,
      opensOn: round.opensOn,
      dueOn: round.dueOn,
    });
  }, [round.periodStart, round.periodEnd, round.opensOn, round.dueOn]);

  const editable = !round.generatedAt;
  const dirty =
    dates.periodStart !== round.periodStart ||
    dates.periodEnd !== round.periodEnd ||
    dates.opensOn !== round.opensOn ||
    dates.dueOn !== round.dueOn;

  return (
    <DetailSection
      title="Round"
      headerActions={
        <div className="flex items-center gap-2">
          <StatusPill tone={STATUS_TONES[round.statusKey] ?? "neutral"} label={round.statusKey} />
          <Button size="sm" variant="outline" onClick={() => setSettingsOpen(true)}>
            Edit settings
          </Button>
          <EditRoundSettingsDialog
            round={round}
            open={settingsOpen}
            onOpenChange={setSettingsOpen}
          />
          {editable && dirty && (
            <Button size="sm" disabled={update.isPending} onClick={() => update.mutate(dates)}>
              Save dates
            </Button>
          )}
          {round.statusKey === "draft" && round.generatedAt && (
            <Button size="sm" onClick={onOpen}>
              Open the round
            </Button>
          )}
          {/*
            The most consequential button in the feature, and it had no confirm
            at all: closing freezes every answer in the round and is the point
            the results become visible to the people reviewed. One click.
          */}
          {round.statusKey === "open" && (
            <ConfirmDialog
              trigger={
                <Button size="sm" variant="outline">
                  Close the round
                </Button>
              }
              title={`Close ${round.name}?`}
              description="Everyone still part-way through loses the chance to finish — nothing further can be answered or changed. Everyone reviewed will be able to read what was said about them. This cannot be undone."
              cancelLabel="Leave it open"
              confirmLabel="Close the round"
              onConfirm={onClose}
            />
          )}
        </div>
      }
    >
      {editable ? (
        <div className="grid gap-4 sm:grid-cols-4">
          <DateField
            id={ids.start}
            label="Period from"
            value={dates.periodStart}
            onChange={(periodStart) => setDates((d) => ({ ...d, periodStart }))}
          />
          <DateField
            id={ids.end}
            label="Period to"
            value={dates.periodEnd}
            onChange={(periodEnd) => setDates((d) => ({ ...d, periodEnd }))}
          />
          <DateField
            id={ids.opens}
            label="Reviewing opens"
            value={dates.opensOn}
            onChange={(opensOn) => setDates((d) => ({ ...d, opensOn }))}
          />
          <DateField
            id={ids.due}
            label="Responses due"
            value={dates.dueOn}
            onChange={(dueOn) => setDates((d) => ({ ...d, dueOn }))}
          />
        </div>
      ) : (
        <dl className="grid gap-4 text-sm sm:grid-cols-4">
          <Fact label="Period reviewed" value={`${round.periodStart} – ${round.periodEnd}`} />
          <Fact label="Reviewing opens" value={round.opensOn} />
          <Fact label="Responses due" value={round.dueOn} />
          <Fact
            label="Progress"
            value={
              round.reviewCount === 0
                ? "Not generated"
                : `${round.completeReviewCount} of ${round.reviewCount} complete`
            }
          />
          {round.excludedReviewerCount > 0 && (
            <Fact label="Excluded reviewers" value={`${round.excludedReviewerCount}`} />
          )}
        </dl>
      )}
    </DetailSection>
  );
}

function DateField({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} type="date" value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
